import {
  Card,
  Container,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import flockApi from 'api/query/flock/flock-api';
import {
  FlockProfileDefaultTraitModel,
  FlockProfileScenarioModel,
  FlockProfileTestModel,
  FlockProfileTestResultModel,
} from 'api/query/flock/models';
import Axios from 'axios';
import CascadingMenu, { CascadingMenuItem } from 'components/cascading-menu';
import {
  CustomisableTrait,
  CustomiseTraitsButton,
  normaliseVisibleTraitIds,
  TraitScenarioOption,
  TraitSelectionId,
} from 'components/customise-traits';
import { FlockProfileCard, FlockProfileCardModel, FlockProfileTrait } from 'components/flock-profile-card';
import LoadingAlert from 'components/loading-alert';
import { UserRole } from 'providers/user/user-role.enum';
import React, { useEffect, useRef, useState } from 'react';
import { useCurrentRoute } from 'react-navi';
import FlockProfileWrapper from '../flock-profile-wrapper';
import '../flock-profiles.scss';
import { FlockProfileDetailPdf } from './flock-profile-detail-pdf';

type FlockProfileDetailProps = {
  flockProfileId: string;
};

type FlockProfileSelectableTrait = CustomisableTrait & {
  trait: FlockProfileTrait;
};

type FlockProfileScenarioOption = TraitScenarioOption;

type FlockProfileDetailRouteData = {
  companyId?: number;
};

const flockProfilePdfTargetId = 'flock-profile-pdf-content';
const flockProfilePdfChartWidth = 910;
const flockProfileMaxTraits = 20;
const importantNoticeParagraphs = [
  [
    'While Sheep Genetics, Meat & Livestock Australia Ltd (MLA) and AGBU have taken all reasonable care to ensure',
    'our methodologies are valid and results are accurate, you should be aware that genomic testing and methodologies',
    'have inherent inaccuracies which, in some cases, cause inaccuracies in the genomic results and our recommendations.',
  ].join(' '),
  [
    'In addition, the samples and information used to prepare this report are obtained from various parties. Neither',
    'Sheep Genetics, MLA nor AGBU can or do verify this information and any inaccuracies in it will impact the accuracy',
    'of the results (e.g. genotypes assigned to the wrong animal can result in misleading results).',
  ].join(' '),
  [
    'Please also be aware that the results and recommendations in this report do not take into account your specific',
    'business interests and circumstances. You should check these against your/the breeders records to verify that any',
    'suggested pedigree allocations are possible. You should also make any other relevant enquiries and take into account',
    'the inherent inaccuracies of genomic testing and methodologies before making decisions concerning your interests or',
    'otherwise relying on these reports in any way.',
  ].join(' '),
  [
    'To the extent permitted by law, this report is provided as is, and Sheep Genetics, MLA and AGBU expressly exclude',
    'all rights, warranties and guarantees regarding the completeness or accuracy of the information in this report.',
  ].join(' '),
];

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat("en-GB").format(date);
};

const getTraitAnalysisId = (result: FlockProfileTestResultModel): number | null => (
  result.traitAnalysisId || result.trait_analysis_id || result.traitAnalysisID || null
);

const getSelectedByDefault = (result: FlockProfileTestResultModel): boolean => Boolean(
  result.selectedByDefault
  || result.isDefaultTrait
  || result.defaultTrait
  || result.selected_by_default
  || result.is_default_trait
);

const mapFlockProfileTrait = (result: FlockProfileTestResultModel): FlockProfileTrait => ({
  traitId: result.traitId,
  traitAnalysisId: getTraitAnalysisId(result),
  selectedByDefault: getSelectedByDefault(result),
  code: result.traitCode,
  label: result.traitName,
  leftLabel: result.rankLowLabel,
  rightLabel: result.rankHighLabel,
  value: result.value,
  percentile: result.rank,
  accuracy: result.accuracy,
  accuracyLabel: result.accuracyLabel,
  color: result.traitGroupColour,
  groupLabel: result.traitGroup,
});

const normaliseAccuracyLabel = (value?: string | null): string => (
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

const isNotApplicableAccuracyLabel = (value?: string | null): boolean => {
  const label = normaliseAccuracyLabel(value);

  return label.length === 0
    || label === 'n/a'
    || label === 'na'
    || label === 'not applicable'
    || label === '-';
};

const isLowAccuracyLabel = (value?: string | null): boolean => normaliseAccuracyLabel(value) === 'low';

const getGroupLabelsByAccuracy = (
  traits: FlockProfileTrait[],
  predicate: (accuracyLabel?: string | null) => boolean,
): string[] => {
  const groupLabels = new Set<string>();

  traits.forEach((trait) => {
    if (predicate(trait.accuracyLabel)) {
      groupLabels.add(trait.groupLabel || 'Other');
    }
  });

  return Array.from(groupLabels).sort((first, second) => first.localeCompare(second));
};

const getTraitGroups = (traits: FlockProfileTrait[]): Map<string, FlockProfileTrait[]> => {
  const traitsByGroup = new Map<string, FlockProfileTrait[]>();

  traits.forEach((trait) => {
    const groupLabel = trait.groupLabel || 'Other';
    const groupTraits = traitsByGroup.get(groupLabel) || [];

    groupTraits.push(trait);
    traitsByGroup.set(groupLabel, groupTraits);
  });

  return traitsByGroup;
};

const getExcludedNotApplicableGroupLabels = (traits: FlockProfileTrait[]): string[] => {
  const groupLabels: string[] = [];

  getTraitGroups(traits).forEach((groupTraits, groupLabel) => {
    if (groupTraits.every((trait) => isNotApplicableAccuracyLabel(trait.accuracyLabel))) {
      groupLabels.push(groupLabel);
    }
  });

  return groupLabels.sort((first, second) => first.localeCompare(second));
};

const filterReportableTraits = (traits: FlockProfileTrait[]): FlockProfileTrait[] => {
  const excludedGroups = new Set<string>();

  getExcludedNotApplicableGroupLabels(traits).forEach((groupLabel) => excludedGroups.add(groupLabel));

  return traits.filter((trait) => !excludedGroups.has(trait.groupLabel || 'Other'));
};

const mapFlockProfileCardModel = (profile: FlockProfileTestModel): FlockProfileCardModel => ({
  flockCode: profile.orderId,
  flockName: profile.companyName || profile.breedFlockName || profile.orderId,
  analysisName: profile.analysisType,
  analysisDate: profile.analysisDate,
  referenceLines: [
    { percentile: 20, stroke: '#a0c468', dashArray: '4 4' },
    { percentile: 10, stroke: '#7ac5d4', dashArray: '4 4' },
    { percentile: 5, stroke: '#cd8ec5', dashArray: '4 4' },
  ],
  traits: filterReportableTraits(profile.results.map(mapFlockProfileTrait)),
});

const getChartNotes = (analysisName: string): string => {
  const databaseName = analysisName || 'selected analysis';

  return [
    'Your Flock Profile values are displayed in the far right hand column. The diagram shows how your flock compares',
    `to flock averages in the ${databaseName} database. Note that the 100th percentile is the bottom value, 50th`,
    `percentile is the average and zero percentile is top value of flocks in ${databaseName}. If you do not see a bar`,
    'for a trait or index, your Flock Profile sits in the 50% percentile. To achieve gain in desired traits or indexes,',
    'select rams with ASBVs higher than your Flock Profile value. Results are subject to change slightly as predictions',
    'update over time. Accuracy indicates the confidence in each trait group result, with high accuracy results generally',
    'being more reliable than medium or low accuracy results.',
  ].join(' ');
};

const getFlockProfileTraitId = (trait: FlockProfileTrait, index: number): string => (
  trait.traitAnalysisId ? `trait-analysis-${trait.traitAnalysisId}` : `${trait.code}-${index}`
);

const logScenarioWarning = (
  operation: string,
  context: { reportId: number; analysisId?: number | null; scenarioId?: number | null },
  error: unknown,
) => {
  console.warn('Flock Profile scenario/default traits could not be loaded.', {
    operation,
    reportId: context.reportId,
    analysisId: context.analysisId,
    scenarioId: context.scenarioId,
    error,
  });
};

const visibleTraitsStorageKey = (reportId: number) => `flock-profile:visible-traits:${reportId}`;
const selectedScenarioStorageKey = (reportId: number) => `flock-profile:selected-scenario:${reportId}`;

const getStoredVisibleTraitIds = (reportId: number): TraitSelectionId[] | null => {
  try {
    const storedValue = window.localStorage.getItem(visibleTraitsStorageKey(reportId));

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);

    return Array.isArray(parsedValue)
      ? parsedValue.filter((value): value is TraitSelectionId => typeof value === 'string' || typeof value === 'number')
      : null;
  } catch {
    return null;
  }
};

const storeVisibleTraitIds = (reportId: number, traitIds: TraitSelectionId[]): void => {
  try {
    window.localStorage.setItem(visibleTraitsStorageKey(reportId), JSON.stringify(traitIds));
  } catch {
    // Local storage can be unavailable in private browsing or restricted environments.
  }
};

const getStoredSelectedScenarioId = (reportId: number): number | null => {
  try {
    const storedValue = window.localStorage.getItem(selectedScenarioStorageKey(reportId));

    if (!storedValue) {
      return null;
    }

    const parsedValue = Number(storedValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const storeSelectedScenarioId = (reportId: number, scenarioId: number): void => {
  try {
    window.localStorage.setItem(selectedScenarioStorageKey(reportId), scenarioId.toString());
  } catch {
    // Local storage can be unavailable in private browsing or restricted environments.
  }
};

const getCompanyId = (value: unknown): number | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  const companyId = Number(value);

  return Number.isInteger(companyId) && companyId > 0
    ? companyId
    : undefined;
};

const getSelectableTraits = (cardModel: FlockProfileCardModel | null): FlockProfileSelectableTrait[] => (
  cardModel?.traits.map((trait, index) => ({
    traitId: getFlockProfileTraitId(trait, index),
    abbrev: trait.code,
    traitName: trait.label,
    trait,
  })) || []
);

const getScenarioLabel = (scenario: FlockProfileScenarioModel): string => {
  const indexLabel = scenario.indexAbbrev || scenario.indexTraitName;

  return indexLabel
    ? `${scenario.scenario} - ${indexLabel}`
    : scenario.scenario;
};

const getScenarioOptions = (scenarios: FlockProfileScenarioModel[]): FlockProfileScenarioOption[] => (
  scenarios.map((scenario) => ({
    scenarioId: scenario.scenarioId,
    label: getScenarioLabel(scenario),
  }))
);

const getScenarioMenuItems = (scenarios: FlockProfileScenarioOption[]): CascadingMenuItem[] => (
  scenarios.map((scenario) => ({
    id: scenario.scenarioId,
    label: scenario.label,
    value: scenario.label,
  }))
);

const getScenarioDefaultTraitIds = (
  selectableTraits: FlockProfileSelectableTrait[],
  defaultTraits: FlockProfileDefaultTraitModel[],
): TraitSelectionId[] => {
  const defaultTraitAnalysisIds = new Set(defaultTraits
    .map((trait) => trait.traitAnalysisId)
    .filter((traitAnalysisId): traitAnalysisId is number => Boolean(traitAnalysisId)));

  if (defaultTraitAnalysisIds.size > 0) {
    const traitAnalysisMatchedIds = selectableTraits
      .filter((selectableTrait) => (
        Boolean(selectableTrait.trait.traitAnalysisId)
        && defaultTraitAnalysisIds.has(Number(selectableTrait.trait.traitAnalysisId))
      ))
      .map((selectableTrait) => selectableTrait.traitId)
      .slice(0, flockProfileMaxTraits);

    if (traitAnalysisMatchedIds.length > 0) {
      return traitAnalysisMatchedIds;
    }
  }

  const defaultTraitIds = new Set(defaultTraits.map((trait) => trait.traitId));

  return selectableTraits
    .filter((selectableTrait) => (
      Boolean(selectableTrait.trait.traitId)
      && defaultTraitIds.has(Number(selectableTrait.trait.traitId))
    ))
    .map((selectableTrait) => selectableTrait.traitId)
    .slice(0, flockProfileMaxTraits);
};

const getDefaultVisibleTraitIds = (selectableTraits: FlockProfileSelectableTrait[]): TraitSelectionId[] => {
  const recommendedTraitIds = selectableTraits
    .filter((selectableTrait) => selectableTrait.trait.selectedByDefault)
    .map((selectableTrait) => selectableTrait.traitId);

  if (recommendedTraitIds.length > 0) {
    return recommendedTraitIds.slice(0, flockProfileMaxTraits);
  }

  // TODO: Remove this fallback once the Flock Profile API returns scenario default trait metadata.
  return normaliseVisibleTraitIds(selectableTraits, null).slice(0, flockProfileMaxTraits);
};

const filterCardModelTraits = (
  cardModel: FlockProfileCardModel,
  visibleTraitIds: TraitSelectionId[],
): FlockProfileCardModel => {
  const visibleTraitIdSet = new Set(visibleTraitIds);

  return {
    ...cardModel,
    traits: cardModel.traits.filter((trait, index) => (
      visibleTraitIdSet.has(getFlockProfileTraitId(trait, index))
    )),
  };
};

export const FlockProfileDetail: React.FC<FlockProfileDetailProps> = ({ flockProfileId }) => {
  const route = useCurrentRoute();
  const routeData = route.data as FlockProfileDetailRouteData | undefined;
  const companyId = getCompanyId(routeData?.companyId);
  const reportId = Number(flockProfileId);
  const isAdminCompanyContext = Boolean(companyId);
  const listBackRoute = isAdminCompanyContext
    ? '/flock-profiles'
    : '/flock-profiles';
  const listBackRouteState = isAdminCompanyContext
    ? { companyId }
    : undefined;
  const scenarioDefaultTraitsSourceRef = useRef<ReturnType<typeof Axios.CancelToken.source> | null>(null);
  const [report, setReport] = useState<FlockProfileTestModel | null>(null);
  const [cardModel, setCardModel] = useState<FlockProfileCardModel | null>(null);
  const [visibleTraitIds, setVisibleTraitIds] = useState<TraitSelectionId[]>([]);
  const [defaultTraitIds, setDefaultTraitIds] = useState<TraitSelectionId[]>([]);
  const [scenarios, setScenarios] = useState<FlockProfileScenarioOption[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
  const [loadingScenarioTraits, setLoadingScenarioTraits] = useState(false);
  const [scenarioRecommendationWarning, setScenarioRecommendationWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    let cancelled = false;

    const loadReport = async () => {
      setLoading(true);
      setError(null);
      setReport(null);
      setCardModel(null);
      setVisibleTraitIds([]);
      setDefaultTraitIds([]);
      setScenarios([]);
      setSelectedScenarioId(null);
      setLoadingScenarioTraits(false);
      setScenarioRecommendationWarning(null);

      if (!Number.isInteger(reportId) || reportId <= 0) {
        setError('Invalid Flock Profile ID.');
        setLoading(false);
        return;
      }

      try {
        const result = isAdminCompanyContext
          ? await flockApi.getAdminCompanyFlockProfileTest(Number(companyId), reportId, source.token)
          : await flockApi.getFlockProfileTest(reportId, source.token);
        if (!cancelled) {
          setReport(result);
          const nextCardModel = mapFlockProfileCardModel(result);
          const nextSelectableTraits = getSelectableTraits(nextCardModel);
          let nextDefaultTraitIds = getDefaultVisibleTraitIds(nextSelectableTraits);
          let nextVisibleTraitIds = nextDefaultTraitIds;
          const storedVisibleTraitIds = getStoredVisibleTraitIds(reportId);

          setCardModel(nextCardModel);

          if (result.analysisId) {
            try {
              const scenarioResults = await flockApi.getFlockProfileScenarios(result.analysisId, source.token);
              const scenarioOptions = getScenarioOptions(scenarioResults);
              const storedScenarioId = getStoredSelectedScenarioId(reportId);
              const nextScenario = scenarioResults.find((scenario) => scenario.scenarioId === storedScenarioId)
                || scenarioResults[0];

              setScenarios(scenarioOptions);
              setSelectedScenarioId(nextScenario?.scenarioId || null);

              if (nextScenario) {
                storeSelectedScenarioId(reportId, nextScenario.scenarioId);
                const scenarioDefaultTraits = await flockApi.getFlockProfileScenarioDefaultTraits(nextScenario.scenarioId, source.token);
                const scenarioDefaultTraitIds = getScenarioDefaultTraitIds(nextSelectableTraits, scenarioDefaultTraits);

                if (scenarioDefaultTraitIds.length > 0) {
                  nextDefaultTraitIds = scenarioDefaultTraitIds;
                  nextVisibleTraitIds = scenarioDefaultTraitIds;
                }
              }
            } catch (scenarioError) {
              if (Axios.isCancel(scenarioError)) {
                throw scenarioError;
              }

              logScenarioWarning('load scenarios/default traits', {
                reportId,
                analysisId: result.analysisId,
              }, scenarioError);
              setScenarioRecommendationWarning('Recommended traits could not be loaded. Fallback traits are being used.');
            }
          }

          if (storedVisibleTraitIds) {
            nextVisibleTraitIds = normaliseVisibleTraitIds(nextSelectableTraits, storedVisibleTraitIds).slice(0, flockProfileMaxTraits);
          }

          setDefaultTraitIds(nextDefaultTraitIds);
          setVisibleTraitIds(nextVisibleTraitIds);
        }
      } catch (requestError) {
        if (!cancelled && !Axios.isCancel(requestError)) {
          setError('Unable to load this Flock Profile. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadReport();

    return () => {
      cancelled = true;
      source.cancel('Flock Profile report unmounted.');
      scenarioDefaultTraitsSourceRef.current?.cancel('Flock Profile scenario default traits request cancelled.');
    };
  }, [companyId, isAdminCompanyContext, reportId]);

  const selectableTraits = React.useMemo(() => getSelectableTraits(cardModel), [cardModel]);
  const normalisedVisibleTraitIds = React.useMemo(() => (
    normaliseVisibleTraitIds(selectableTraits, visibleTraitIds).slice(0, flockProfileMaxTraits)
  ), [selectableTraits, visibleTraitIds]);
  const visibleCardModel = React.useMemo(() => (
    cardModel ? filterCardModelTraits(cardModel, normalisedVisibleTraitIds) : null
  ), [cardModel, normalisedVisibleTraitIds]);
  const saveVisibleTraitIds = (traitIds: TraitSelectionId[]) => {
    const nextVisibleTraitIds = normaliseVisibleTraitIds(selectableTraits, traitIds).slice(0, flockProfileMaxTraits);

    storeVisibleTraitIds(reportId, nextVisibleTraitIds);
    setVisibleTraitIds(nextVisibleTraitIds);
  };
  const changeScenario = async (scenarioId: number): Promise<TraitSelectionId[]> => {
    scenarioDefaultTraitsSourceRef.current?.cancel('A newer Flock Profile scenario default traits request started.');

    const source = Axios.CancelToken.source();
    scenarioDefaultTraitsSourceRef.current = source;

    try {
      const scenarioDefaultTraits = await flockApi.getFlockProfileScenarioDefaultTraits(scenarioId, source.token);

      if (scenarioDefaultTraitsSourceRef.current !== source) {
        return normalisedVisibleTraitIds;
      }

      const scenarioDefaultTraitIds = getScenarioDefaultTraitIds(selectableTraits, scenarioDefaultTraits);
      const nextDefaultTraitIds = scenarioDefaultTraitIds.length > 0
        ? scenarioDefaultTraitIds
        : getDefaultVisibleTraitIds(selectableTraits);

      storeSelectedScenarioId(reportId, scenarioId);
      setSelectedScenarioId(scenarioId);
      setDefaultTraitIds(nextDefaultTraitIds);
      setScenarioRecommendationWarning(null);

      return nextDefaultTraitIds;
    } catch (scenarioError) {
      if (Axios.isCancel(scenarioError)) {
        return normalisedVisibleTraitIds;
      }

      logScenarioWarning('load scenario default traits', {
        reportId,
        analysisId: report?.analysisId,
        scenarioId,
      }, scenarioError);
      setScenarioRecommendationWarning('Scenario recommended traits could not be loaded. Your current traits have been kept.');

      return normalisedVisibleTraitIds.length > 0 ? normalisedVisibleTraitIds : defaultTraitIds;
    } finally {
      if (scenarioDefaultTraitsSourceRef.current === source) {
        scenarioDefaultTraitsSourceRef.current = null;
      }
    }
  };
  const changeInterestedScenario = async (scenario: CascadingMenuItem) => {
    const scenarioId = Number(scenario.id);

    if (
      !Number.isFinite(scenarioId)
      || scenarioId === selectedScenarioId
      || loadingScenarioTraits
      || selectableTraits.length === 0
    ) {
      return;
    }

    setLoadingScenarioTraits(true);

    try {
      const scenarioTraitIds = await changeScenario(scenarioId);
      saveVisibleTraitIds(scenarioTraitIds);
    } finally {
      setLoadingScenarioTraits(false);
    }
  };
  const selectedScenario = React.useMemo(() => (
    scenarios.find((scenario) => scenario.scenarioId === selectedScenarioId)
  ), [scenarios, selectedScenarioId]);
  const scenarioMenuItems = React.useMemo(() => getScenarioMenuItems(scenarios), [scenarios]);
  const allReportTraits = React.useMemo(() => (
    report ? report.results.map(mapFlockProfileTrait) : []
  ), [report]);
  const excludedAccuracyGroupLabels = React.useMemo(() => (
    getExcludedNotApplicableGroupLabels(allReportTraits)
  ), [allReportTraits]);
  const visibleLowAccuracyGroupLabels = React.useMemo(() => (
    visibleCardModel ? getGroupLabelsByAccuracy(visibleCardModel.traits, isLowAccuracyLabel) : []
  ), [visibleCardModel]);

  return (
    <FlockProfileWrapper
      backLocationName='List'
      backRoute={listBackRoute}
      backRouteState={listBackRouteState}
      roles={isAdminCompanyContext ? [UserRole.Admin] : undefined}
      title='Flock Profile'
    >
      <Container className='sg-flock-profile__container'>
        {loading ? (
          <Card className='sg-flock-profile-report__state'>
            <LoadingAlert message='Loading Flock Profile…' />
          </Card>
        ) : error ? (
          <Card className='sg-flock-profile-report__state'>
            <Alert severity='error'>{error}</Alert>
          </Card>
        ) : !report || !cardModel || !visibleCardModel ? (
          <Card className='sg-flock-profile-report__state'>
            <Alert severity='warning'>No Flock Profile report was found for Profile ID {flockProfileId}.</Alert>
          </Card>
        ) : visibleCardModel.traits.length === 0 ? (
          <Card className='sg-flock-profile-report__state'>
            <Alert severity='warning'>No reportable trait groups are available for this Flock Profile because all trait group accuracy values are N/A.</Alert>
          </Card>
        ) : (
          <div className='sg-flock-profile-report__content'>

            <Card className='sg-flock-profile-report__context'>
              <div>
                <div className='sg-flock-profile-report__context-label'>Profile ID</div>
                <div className='sg-flock-profile-report__context-value'>{reportId}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Order ID</div>
                <div className='sg-flock-profile-report__context-value'>{report.orderId}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Company</div>
                <div className='sg-flock-profile-report__context-value'>{report.companyName || '-'}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Genomic Provider</div>
                <div className='sg-flock-profile-report__context-value'>{report.genomicCompanyName || report.genomicCompanyCode || '-'}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Drop Year</div>
                <div className='sg-flock-profile-report__context-value'>{report.dropYearLabel || '-'}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Analysis</div>
                <div className='sg-flock-profile-report__context-value'>{report.analysisType}</div>
              </div>
              <div>
                <div className='sg-flock-profile-report__context-label'>Analysis Date</div>
                <div className='sg-flock-profile-report__context-value'>{formatDate(report.analysisDate)}</div>
              </div>
              {scenarios.length > 0 && (
                <div className='sg-flock-profile-report__scenario-menu'>
                  <span className='sg-flock-profile-report__scenario-label'>I&apos;m interested in</span>
                  <CascadingMenu
                    key={`flock-profile-scenario-${selectedScenarioId || 'none'}`}
                    id='flock-profile-interested-in'
                    items={scenarioMenuItems}
                    selected={changeInterestedScenario}
                    default={selectedScenario?.label}
                  />
                </div>
              )}
            </Card>

            <div className="sg-flock-profile-report-pdf">
              {scenarioRecommendationWarning && (
                <Alert className='sg-flock-profile-report__scenario-warning' severity='warning'>
                  {scenarioRecommendationWarning}
                </Alert>
              )}
              {excludedAccuracyGroupLabels.length > 0 && (
                <Alert className='sg-flock-profile-report__accuracy-warning' severity='warning'>
                  Trait groups with N/A accuracy are not shown on this Flock Profile: {excludedAccuracyGroupLabels.join(', ')}.
                </Alert>
              )}
              {visibleLowAccuracyGroupLabels.length > 0 && (
                <Alert className='sg-flock-profile-report__accuracy-warning' severity='info'>
                  Low accuracy trait groups are shown in red and should be interpreted with caution: {visibleLowAccuracyGroupLabels.join(', ')}.
                </Alert>
              )}
              <CustomiseTraitsButton
                availableTraits={selectableTraits}
                selectedTraitIds={normalisedVisibleTraitIds}
                defaultTraitIds={defaultTraitIds}
                disabled={selectableTraits.length === 0}
                loading={loadingScenarioTraits}
                onSave={saveVisibleTraitIds}
                maxTraits={flockProfileMaxTraits}
                title="Customise Flock Profile traits"
                scenarios={scenarios}
                selectedScenarioId={selectedScenarioId}
                onScenarioChange={changeScenario}
              />
              <FlockProfileDetailPdf
                cardModel={visibleCardModel}
                report={report}
                reportId={reportId}
                selectedIndexLabel={selectedScenario?.label}
                targetId={flockProfilePdfTargetId}
              />
            </div>

            <div className='sg-flock-profile-report__pdf-source' id={flockProfilePdfTargetId}>

              <FlockProfileCard
                model={visibleCardModel}
                chartMode='desktop'
                desktopChartWidth={flockProfilePdfChartWidth}
                showHeader={false}
              />
              <div className='sg-flock-profile-report__pdf-sections'>
                <section className='sg-flock-profile-report__pdf-chart-notes'>
                  <dl className='sg-flock-profile-report__pdf-details'>
                    <div>
                      <dt>Client:</dt>
                      <dd>{report.companyName || report.orderId || '-'}</dd>
                    </div>
                    <div>
                      <dt>Date:</dt>
                      <dd>{formatDate(report.analysisDate)}</dd>
                    </div>
                    <div>
                      <dt>Drop Year:</dt>
                      <dd>{report.dropYearLabel || '-'}</dd>
                    </div>
                    <div>
                      <dt>Analysis:</dt>
                      <dd>{report.analysisType || '-'}</dd>
                    </div>
                  </dl>
                  <p>{getChartNotes(report.analysisType)}</p>
                </section>
                <section className='sg-flock-profile-report__pdf-important-notice'>
                  <h3>IMPORTANT NOTICE</h3>
                  <h4>General</h4>
                  {importantNoticeParagraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </section>
              </div>
            </div>

            <div>
              <FlockProfileCard model={visibleCardModel} />
            </div>

            <div className='sg-flock-profile-report__page-sections'>
              <section className='sg-flock-profile-report__page-chart-notes'>
                <p>{getChartNotes(report.analysisType)}</p>
                <h3>IMPORTANT NOTICE</h3>
                <h4>General</h4>
                {importantNoticeParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            </div>
          </div>
        )}
      </Container>
    </FlockProfileWrapper>
  );
};

export default FlockProfileDetail;
